import {
  AlertTriangle,
  Ban,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  Check,
  CheckCheck,
  CircleDashed,
  CircleDollarSign,
  CircleOff,
  Clock,
  Eye,
  FileClock,
  Handshake,
  Hourglass,
  type LucideIcon,
  MessageSquareText,
  PauseCircle,
  Search,
  Sparkles,
  UserCheck,
  XCircle,
} from "lucide-react";
import type {
  AppointmentStatus,
  ConversationAiStatus,
  ConversationControl,
  DeliveryStatus,
  HandoffReason,
  LeadImportStatus,
  LeadRowStatus,
  MessageAiStatus,
  NextActionKind,
  OfferStatus,
  OpportunitySource,
  OpportunityStage,
  OpportunityStatus,
  PurchaseListItem,
  SourceMetrics,
} from "@/api/types";

/**
 * Centralized status vocabulary. Components render states through these maps and never branch
 * on raw status strings for labels, colors, icons or allowed actions.
 */
export type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "ai";

export interface StatusConfig {
  label: string;
  tone: Tone;
  icon?: LucideIcon;
  description?: string;
}

export const TONE_CLASSES: Record<Tone, { text: string; bg: string; dot: string; border: string }> = {
  neutral: { text: "text-muted-foreground", bg: "bg-muted", dot: "bg-muted-foreground/60", border: "border-border" },
  success: { text: "text-success", bg: "bg-success-muted", dot: "bg-success", border: "border-success/30" },
  warning: { text: "text-warning", bg: "bg-warning-muted", dot: "bg-warning", border: "border-warning/30" },
  danger: { text: "text-danger", bg: "bg-danger-muted", dot: "bg-danger", border: "border-danger/30" },
  info: { text: "text-info", bg: "bg-info-muted", dot: "bg-info", border: "border-info/30" },
  ai: { text: "text-ai", bg: "bg-ai-muted", dot: "bg-ai", border: "border-ai/30" },
};

// ── opportunity stage ───────────────────────────────────────────────────────
export type OpportunityActionId =
  | "open"
  | "take_over"
  | "book_appointment"
  | "call_seller"
  | "approve_offer"
  | "set_offer"
  | "edit_vehicle"
  | "assign_buyer"
  | "disqualify"
  | "send_to_rooftop"
  | "record_purchase";

const EARLY: OpportunityActionId[] = ["open", "take_over", "call_seller", "edit_vehicle", "assign_buyer", "disqualify", "send_to_rooftop"];
const OFFERABLE: OpportunityActionId[] = [...EARLY, "set_offer", "approve_offer", "book_appointment"];
const LATE: OpportunityActionId[] = ["open", "take_over", "call_seller", "assign_buyer", "record_purchase", "book_appointment"];

export const STAGE: Record<OpportunityStage, StatusConfig & { actions: OpportunityActionId[] }> = {
  NEW: { label: "New", tone: "info", actions: EARLY, description: "Just arrived; Rogue will make first contact." },
  IDENTIFYING: { label: "Identifying", tone: "neutral", actions: EARLY, description: "Confirming the vehicle." },
  ENRICHING: { label: "Enriching", tone: "neutral", actions: EARLY, description: "Collecting vehicle details." },
  VALUED: { label: "Valued", tone: "neutral", actions: OFFERABLE, description: "An approved valuation exists." },
  QUALIFYING: { label: "Qualifying", tone: "neutral", actions: OFFERABLE, description: "Confirming intent, condition and title." },
  ENGAGED: { label: "Engaged", tone: "info", actions: OFFERABLE, description: "Seller is actively responding." },
  OFFER_SENT: { label: "Offer sent", tone: "info", actions: OFFERABLE, description: "Waiting on the seller's answer." },
  NEGOTIATING: { label: "Negotiating", tone: "warning", actions: OFFERABLE, description: "Working the gap to the seller's number." },
  OFFER_ACCEPTED: {
    label: "Offer accepted",
    tone: "success",
    actions: ["open", "take_over", "call_seller", "assign_buyer", "book_appointment"],
    description: "The seller agreed to a price. Book an in-store appointment (no pickup).",
  },
  APPOINTMENT_SET: { label: "Appt set", tone: "success", actions: LATE, description: "Appointment scheduled." },
  APPOINTMENT_CONFIRMED: { label: "Appt confirmed", tone: "success", actions: LATE, description: "Seller confirmed the appointment." },
  SHOWED: { label: "Showed", tone: "success", actions: LATE, description: "Seller arrived with the vehicle." },
  APPRAISED: { label: "Appraised", tone: "success", actions: LATE, description: "Vehicle inspected on site." },
  PURCHASED: { label: "Purchased", tone: "success", actions: ["open"], description: "Vehicle acquired." },
};

export const STATUS: Record<OpportunityStatus, StatusConfig> = {
  OPEN: { label: "Open", tone: "neutral" },
  DISQUALIFIED: { label: "Disqualified", tone: "neutral", icon: Ban },
  OPTED_OUT: { label: "Opted out", tone: "danger", icon: CircleOff, description: "Seller asked not to be contacted." },
  UNRESPONSIVE: { label: "Unresponsive", tone: "neutral", icon: Hourglass },
  LOST: { label: "Lost", tone: "neutral", icon: XCircle },
  EXPIRED: { label: "Expired", tone: "neutral", icon: Clock },
  DUPLICATE: { label: "Duplicate", tone: "neutral" },
};

/** A finished deal: purchased, or closed (lost, disqualified…). Read-only apart from reopening a closed one. */
export function isFinished(stage: OpportunityStage, status: OpportunityStatus): boolean {
  return status !== "OPEN" || stage === "PURCHASED";
}

/** Stages where the price is agreed (or the car bought): no more max offers or offers. */
const PRICE_SETTLED: readonly OpportunityStage[] = ["OFFER_ACCEPTED", "APPOINTMENT_SET", "APPOINTMENT_CONFIRMED", "SHOWED", "APPRAISED", "PURCHASED"];

/**
 * Whether the max offer can be set and offers made or approved: any open lead until the seller
 * accepts, whatever its stage (the API offer can fail while the lead is still being enriched).
 */
export function offersOpen(stage: OpportunityStage, status: OpportunityStatus): boolean {
  return status === "OPEN" && !PRICE_SETTLED.includes(stage);
}

/** What to tell staff on a finished deal (null while it's being worked). */
export function finishedNotice(stage: OpportunityStage, status: OpportunityStatus): string | null {
  if (status !== "OPEN") return `${STATUS[status].label}. Rogue has stopped working this opportunity; reopen it to make changes.`;
  if (stage === "PURCHASED") return "Purchased. This deal is done and Rogue has stopped.";
  return null;
}

/** Actions allowed for a record: stage-appropriate, and none on closed records. */
export function allowedOpportunityActions(stage: OpportunityStage, status: OpportunityStatus): Set<OpportunityActionId> {
  if (status !== "OPEN") return new Set(["open"]);
  return new Set(STAGE[stage].actions);
}

// ── Rogue / conversation ────────────────────────────────────────────────────
export const CONVERSATION_AI: Record<ConversationAiStatus, StatusConfig & { active: boolean }> = {
  QUEUED: { label: "Rogue queued…", tone: "ai", icon: Hourglass, active: true, description: "Waiting a moment for more seller messages." },
  PROCESSING: { label: "Rogue is working", tone: "ai", icon: Sparkles, active: true, description: "Reading the conversation and deciding the next step." },
  IDLE: { label: "Rogue is ready", tone: "neutral", icon: CircleDashed, active: false },
  FAILED: { label: "Rogue processing failed", tone: "danger", icon: AlertTriangle, active: false, description: "No reply was sent." },
};

export const CONTROL: Record<ConversationControl, StatusConfig> = {
  ROGUE: { label: "Rogue is handling this", tone: "ai", icon: Sparkles },
  HUMAN: { label: "You're in control — Rogue is paused", tone: "warning", icon: PauseCircle, description: "Rogue won't reply automatically." },
};

export const MESSAGE_AI: Record<MessageAiStatus, StatusConfig | null> = {
  PENDING: { label: "Queued for Rogue", tone: "ai", icon: Hourglass },
  PROCESSING: { label: "Rogue processing", tone: "ai", icon: Sparkles },
  PROCESSED: { label: "Processed", tone: "neutral", icon: Check },
  FAILED: { label: "Not processed", tone: "danger", icon: AlertTriangle },
  SKIPPED: null,
};

export const DELIVERY: Record<DeliveryStatus, StatusConfig> = {
  NOT_SENT: { label: "Draft — not sent", tone: "warning", icon: FileClock },
  QUEUED: { label: "Queued", tone: "neutral", icon: Clock },
  SENT: { label: "Sent", tone: "neutral", icon: Check },
  DELIVERED: { label: "Delivered", tone: "neutral", icon: CheckCheck },
  READ: { label: "Read", tone: "info", icon: Eye },
  FAILED: { label: "Failed", tone: "danger", icon: AlertTriangle },
};

/** Why the backend held back a Rogue draft (errorCode SUPPRESSED_*). */
export const DRAFT_REASON: Record<string, string> = {
  SUPPRESSED_UNAPPROVED_AMOUNT: "Mentions a price that isn't an approved offer.",
  SUPPRESSED_CASH_MENTION: "Mentions cash. The dealership never offers cash.",
  SUPPRESSED_AUTO_REPLY_DISABLED: "Store requires review before Rogue replies.",
  SUPPRESSED_TOO_LONG: "Longer than the store's message limit.",
  SUPPRESSED_SELLER_OPTED_OUT: "Seller has opted out.",
  SUPPRESSED_STOP_ACTION: "Seller asked to stop.",
  SUPPRESSED_EMPTY: "Empty reply.",
};

// ── offers / appointments / purchases ───────────────────────────────────────
export const OFFER: Record<OfferStatus, StatusConfig> = {
  RECOMMENDED: { label: "Rogue recommendation", tone: "ai", icon: Sparkles },
  PENDING_APPROVAL: { label: "Needs approval", tone: "warning", icon: FileClock },
  APPROVED: { label: "Approved", tone: "success", icon: Check },
  BLOCKED: { label: "Blocked by policy", tone: "danger", icon: Ban },
  SENT: { label: "Sent to seller", tone: "info", icon: MessageSquareText },
  ACCEPTED: { label: "Accepted", tone: "success", icon: Handshake },
  DECLINED: { label: "Declined", tone: "neutral", icon: XCircle },
  WITHDRAWN: { label: "Withdrawn", tone: "neutral" },
};

export const POLICY_VIOLATION: Record<string, string> = {
  NO_APPROVED_VALUATION: "No approved valuation",
  EXCEEDS_MAX_OFFER: "Above the store maximum",
  NOT_A_MULTIPLE_OF_INCREMENT: "Not on the store's offer increment",
  NON_POSITIVE_AMOUNT: "Invalid amount",
  VALUATION_EXPIRED: "Valuation expired",
};

export const APPOINTMENT: Record<AppointmentStatus, StatusConfig & { actions: Array<"confirm" | "reschedule" | "cancel" | "arrived" | "no_show"> }> = {
  PROPOSED: {
    label: "Needs confirmation",
    tone: "warning",
    icon: CalendarClock,
    actions: ["confirm", "reschedule", "cancel"],
    description: "Proposed by Rogue; a person must confirm.",
  },
  SCHEDULED: { label: "Booked", tone: "info", icon: CalendarClock, actions: ["confirm", "reschedule", "cancel", "arrived", "no_show"] },
  CONFIRMED: { label: "Confirmed", tone: "success", icon: CalendarCheck, actions: ["reschedule", "cancel", "arrived", "no_show"] },
  SHOWED: { label: "Arrived", tone: "success", icon: UserCheck, actions: [] },
  NO_SHOW: { label: "No-show", tone: "danger", icon: CalendarX, actions: ["reschedule"] },
  CANCELLED: { label: "Cancelled", tone: "neutral", icon: XCircle, actions: ["reschedule"] },
};

export const INVENTORY_STATUS: Record<NonNullable<PurchaseListItem["inventoryStatus"]>, StatusConfig> = {
  ACQUIRED: { label: "Acquired", tone: "info" },
  IN_RECON: { label: "In recon", tone: "warning" },
  FRONTLINE: { label: "Frontline", tone: "success" },
  SOLD_RETAIL: { label: "Sold retail", tone: "success" },
  WHOLESALED: { label: "Wholesaled", tone: "neutral" },
};

export const NEXT_ACTION: Record<NextActionKind, { tone: Tone; icon: LucideIcon }> = {
  APPROVE_OFFER: { tone: "warning", icon: CircleDollarSign },
  RAISE_OFFER: { tone: "warning", icon: CircleDollarSign },
  FOLLOW_UP: { tone: "neutral", icon: MessageSquareText },
  BOOK_APPOINTMENT: { tone: "info", icon: CalendarClock },
  CONFIRM_APPOINTMENT: { tone: "info", icon: CalendarCheck },
  REVIEW_DRAFT: { tone: "warning", icon: FileClock },
  RETRY_AI: { tone: "danger", icon: AlertTriangle },
  TAKE_OVER: { tone: "warning", icon: UserCheck },
  SET_OFFER: { tone: "warning", icon: CircleDollarSign },
  HANDOFF: { tone: "warning", icon: UserCheck },
  START_CONVERSATION: { tone: "warning", icon: MessageSquareText },
  NONE: { tone: "neutral", icon: Search },
};

// ── sources ─────────────────────────────────────────────────────
export const SOURCE_LABEL: Record<OpportunitySource, string> = {
  KBB_ICO: "KBB ICO",
  CRM: "CRM",
  FACEBOOK: "Facebook",
};

export const SOURCE_CONNECTION: Record<SourceMetrics["connection"], StatusConfig> = {
  CONNECTED: { label: "Connected", tone: "success" },
  NOT_CONNECTED: { label: "Not connected", tone: "neutral" },
  ERROR: { label: "Error", tone: "danger" },
  MANUAL: { label: "Manual entry", tone: "neutral" },
};

/** Rogue Score band → wording and tone. Scores come from the backend; never computed here. */
export function scoreBand(score: number | null | undefined): { label: string; tone: Tone } {
  if (score === null || score === undefined) return { label: "Not scored", tone: "neutral" };
  if (score >= 85) return { label: "High purchase likelihood", tone: "success" };
  if (score >= 65) return { label: "Promising", tone: "info" };
  if (score >= 40) return { label: "Needs work", tone: "warning" };
  return { label: "Low likelihood", tone: "neutral" };
}

export const LEAD_IMPORT: Record<LeadImportStatus, StatusConfig> = {
  DRAFT: { label: "Needs mapping", tone: "warning", description: "Uploaded. Confirm the column mapping to start the import." },
  QUEUED: { label: "Queued", tone: "info", description: "Waiting to be processed." },
  PROCESSING: { label: "Importing", tone: "ai", description: "Creating opportunities." },
  COMPLETED: { label: "Completed", tone: "success" },
  FAILED: { label: "Failed", tone: "danger", description: "Processing stopped; rows already imported were kept." },
  CANCELLED: { label: "Cancelled", tone: "neutral", description: "Stopped before every row was processed." },
};

export const LEAD_ROW: Record<LeadRowStatus, StatusConfig> = {
  PENDING: { label: "Pending", tone: "neutral" },
  CREATED: { label: "Created", tone: "success", description: "A new opportunity was created." },
  DUPLICATE: { label: "Duplicate", tone: "info", description: "Already in Rogue: no second opportunity was created." },
  FAILED: { label: "Failed", tone: "danger" },
  SKIPPED: { label: "Skipped", tone: "neutral" },
};

export const HANDOFF_REASON: Record<HandoffReason, { label: string; description: string }> = {
  SMALL_GAP: { label: "Small gap", description: "The seller asks a little more than the max offer. Your call." },
  DEAD_END: { label: "Dead end", description: "Rogue offered its maximum and the seller won't move." },
  ESCALATED: { label: "Needs a person", description: "The seller asked for a person, or Rogue flagged something it can't handle." },
  AGREEMENT: { label: "Agreement", description: "The seller agreed to a price. Book an in-store appointment (no pickup)." },
};
